import { z } from 'zod';
import { syncJobStatusSchema } from '../schemas/sync-job.js';
import { idParamSchema, listQueryBase, multiFilter, paginatedResponse, paginationQuerySchema, } from './_shared.js';
/** Trigger (`manual` admin click) vs (`scheduled` 15-min tick). */
export const sweepTriggerSchema = z.enum(['manual', 'scheduled']);
export const sweepStatusSchema = z.enum(['running', 'succeeded', 'failed']);
/** One reconcile sweep. Spec 2026-06-15 §3. */
export const syncSweepSchema = z.object({
    id: z.string().uuid(),
    trigger: sweepTriggerSchema,
    triggeredBy: z.string().uuid().nullable(),
    /**
     * Display label for the actor who triggered a manual sweep — the user's
     * email, resolved via a LEFT JOIN on `users`. Null for scheduled sweeps
     * (no actor) and for manual sweeps whose actor row was since deleted. The
     * UI renders this instead of the raw `triggeredBy` UUID.
     */
    triggeredByEmail: z.string().nullable(),
    status: sweepStatusSchema,
    reposChecked: z.number().int().nullable(),
    reposEnqueued: z.number().int().nullable(),
    error: z.string().nullable(),
    startedAt: z.string().datetime(),
    finishedAt: z.string().datetime().nullable(),
});
/** Per-repo job within a sweep (drill-down). Extends the sync-job admin shape. */
export const sweepJobSchema = z.object({
    id: z.string().uuid(),
    sourceId: z.string().uuid(),
    sourceName: z.string().nullable(),
    headSha: z.string().min(1),
    status: syncJobStatusSchema,
    pluginsAdded: z.number().int().nullable(),
    pluginsUpdated: z.number().int().nullable(),
    pluginsYanked: z.number().int().nullable(),
    startedAt: z.string().datetime().nullable(),
    finishedAt: z.string().datetime().nullable(),
    error: z.string().nullable(),
});
// POST /api/admin/sync/sweep
export const triggerSweepResponseSchema = z.object({ sweepId: z.string().uuid() });
// POST /api/admin/sync/sources/:id
export const forceSyncSourceParamsSchema = idParamSchema;
export const forceSyncSourceResponseSchema = z.object({
    sourceId: z.string().uuid(),
    jobId: z.string().min(1),
    headSha: z.string().min(1),
});
// POST /api/admin/sync/contributions
/**
 * Operator trigger for the nightly `collect-contributions` worker. Returns
 * the enqueued BullMQ job id — the run itself is asynchronous (26 repos × 2
 * paged ADO reads is well past a request budget), which is why this is a 202
 * and not a synchronous result.
 */
export const collectContributionsResponseSchema = z.object({ jobId: z.string().min(1) });
// GET /api/admin/sync/sweeps
/** Canonical pagination query for the sweep-history listing. */
export const listSweepsQuerySchema = listQueryBase.extend({
    status: multiFilter(['running', 'succeeded', 'failed']),
});
export const listSweepsResponseSchema = paginatedResponse(syncSweepSchema);
export const exportSweepsQuerySchema = listSweepsQuerySchema.omit({ page: true, pageSize: true });
// GET /api/admin/sync/sweeps/:id/jobs
export const sweepJobsParamsSchema = idParamSchema;
/** Canonical pagination query for the per-sweep jobs drill-down. */
export const listSweepJobsQuerySchema = paginationQuerySchema;
export const listSweepJobsResponseSchema = paginatedResponse(sweepJobSchema);
/**
 * One persisted validator finding behind a `validation_failed` outcome.
 * Mirrors the worker's `SyncJobValidationIssue` jsonb shape — `scanner` is a
 * free string (the column survives scanner-set growth), and the matched
 * cleartext is already redacted at source (`secret.ts:redactCleartext`).
 */
export const syncJobValidationIssueSchema = z.object({
    severity: z.enum(['error', 'warning']),
    scanner: z.string(),
    message: z.string(),
    file: z.string().optional(),
});
/**
 * Per-plugin outcome of one `sync-repo` run — the structured, queryable detail
 * behind the lossy 200-char `sync_jobs.error`. `pluginName` is null on a
 * bad-JSON manifest; `issues` carries the redacted findings for a
 * `validation_failed` outcome and is null otherwise; `message` is a
 * human/agent-actionable one-liner (plugin + scanner + file + fix).
 */
export const syncJobPluginResultSchema = z.object({
    id: z.string().uuid(),
    syncJobId: z.string().uuid(),
    sourcePath: z.string(),
    pluginName: z.string().nullable(),
    outcome: z.enum(['synced', 'updated', 'yanked', 'validation_failed', 'collision', 'bad_json']),
    issues: z.array(syncJobValidationIssueSchema).nullable(),
    message: z.string().nullable(),
    createdAt: z.string().datetime(),
});
// GET /api/admin/sync/jobs/:id/plugin-results
export const syncJobPluginResultsParamsSchema = idParamSchema;
/** Canonical pagination query for the per-job plugin-results drill-down. */
export const listSyncJobPluginResultsQuerySchema = paginationQuerySchema;
export const listSyncJobPluginResultsResponseSchema = paginatedResponse(syncJobPluginResultSchema);
//# sourceMappingURL=admin-sync.js.map